import { useEffect, useState } from "react";
import {
  Button,
  Input,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@flint/ui";

export type OptionKind = "category" | "subCategory" | "collection";

/** What each kind is called in prose — the dialog's error copy reads it. */
const NOUN: Record<OptionKind, string> = {
  category: "category",
  subCategory: "sub-category",
  collection: "collection",
};

const COPY: Record<OptionKind, { title: string; label: string; placeholder: string; hint: string }> =
  {
    category: {
      title: "New category",
      label: "Category name *",
      placeholder: "Jackets",
      hint: "Applied to the product you're creating. It becomes permanent — and appears here for everyone — once you save the product. Leave without saving and it's gone.",
    },
    subCategory: {
      title: "New sub-category",
      label: "Sub-category name *",
      placeholder: "Full sleeve",
      hint: "Filed under the category you've picked, and applied to the product you're creating. It becomes permanent — and appears here for everyone — once you save the product. Leave without saving and it's gone.",
    },
    collection: {
      title: "New collection",
      label: "Collection name *",
      placeholder: "SS26",
      hint: "Applied to the product you're creating. It becomes permanent — and appears here for everyone — once you save the product. Leave without saving and it's gone.",
    },
  };

export interface NewOptionDialogProps {
  kind: OptionKind;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Values already in the picker — used to reject duplicates. */
  existing: readonly string[];
  onCreate: (option: { name: string }) => void;
}

/**
 * Add a category, sub-category or collection without leaving the product form.
 *
 * The taxonomy levels have no master table — `items.category` and
 * `items.sub_category` are free text — so a value added here is applied to the
 * product you're creating and becomes part of the picker vocabulary for
 * everyone the moment that product saves. Until then it lives in React state
 * and a refresh discards it, which the dialog copy states outright.
 *
 * For `subCategory` the caller passes only the *selected category's* children
 * as `existing`, so the same name may legitimately be created under two
 * parents — the pairing is per product row, not global.
 *
 * There is deliberately no description field: with no table to hold it, one
 * would collect input we then throw away.
 */
export function NewOptionDialog({
  kind,
  open,
  onOpenChange,
  existing,
  onCreate,
}: NewOptionDialogProps) {
  const copy = COPY[kind];
  const noun = NOUN[kind];
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName("");
      setError(null);
    }
  }, [open]);

  const save = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError(`Give the ${noun} a name.`);
      return;
    }
    if (existing.some((e) => e.toLowerCase() === trimmed.toLowerCase())) {
      setError(`That ${noun} already exists.`);
      return;
    }
    onCreate({ name: trimmed });
    onOpenChange(false);
  };

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.hint}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-1">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="new-option-name" className="text-sm font-medium leading-[18px] text-ink-2">
              {copy.label}
            </label>
            <Input
              id="new-option-name"
              value={name}
              autoFocus
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  save();
                }
              }}
              placeholder={copy.placeholder}
              maxLength={48}
            />
            {error ? <p className="text-xs text-bad">{error}</p> : null}
          </div>

        </div>

        <DialogFooter>
          <Button intent="ghost" size="md" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button intent="primary" size="md" onClick={save}>
            Add {noun}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
